'use client';

import { useState } from 'react';

import { Button } from '@ria-client/tw-shadcn/components/button';
import { BrandIcon, type BrandIconName } from '@ria-client/tw-shared-ui';
import type { NodeProps } from '../types';
import { ContentLink } from './lib/content-link';

export function SMBInfoGatheringCta({
  props,
}: NodeProps<{
  headline: string;
  answers: { answer: string; destination?: string }[];
  ctaText?: string;
  icon: BrandIconName;
  subText?: string;
}>) {
  const { headline, answers = [], ctaText = 'learn more', icon, subText } = props;
  const [selectedAnswer, setSelectedAnswer] = useState(answers?.[0]);

  return (
    // MUI elevation 1: @mui/material/styles/shadows.
    <div className="my-8 flex flex-col gap-4 shadow-[0px_2px_1px_-1px_rgb(0_0_0/0.2),0px_1px_1px_0px_rgb(0_0_0/0.14),0px_1px_3px_0px_rgb(0_0_0/0.12)] md:flex-row">
      <div className="flex flex-1 flex-col items-start justify-between gap-4 bg-accent p-7 md:flex-row md:gap-0 md:p-8">
        {icon ? (
          <div className="flex flex-1 self-center md:mr-4 md:self-start">
            <div className="flex size-[60px] items-center justify-center rounded-full bg-accent-foreground [&_img]:brightness-0 [&_img]:invert">
              <BrandIcon name={icon} size={40} />
            </div>
          </div>
        ) : null}
        <div className="flex flex-[3] flex-col">
          <span className="typography-text-bold">{headline}</span>
          <span className="typography-text">{subText}</span>
        </div>
      </div>
      <div className="mb-2 flex flex-1 flex-col p-7 md:p-8">
        {answers.length > 0 ? (
          <div className="relative">
            <select
              aria-label="Select an answer"
              className="h-11 w-full min-w-[136px] appearance-none border border-input bg-background px-4 py-2 text-foreground outline-none focus-visible:focus-ring"
              id="answer"
              name="answer"
              onChange={event => {
                const answer = answers.find(item => item.answer === event.target.value);
                if (answer) setSelectedAnswer(answer);
              }}
              value={selectedAnswer?.answer || ''}
            >
              {answers.map(answer => (
                <option key={answer.answer} value={answer.answer}>
                  {answer.answer}
                </option>
              ))}
            </select>
            <svg
              aria-hidden="true"
              className="pointer-events-none absolute top-1/2 right-[5%] h-2.5 w-[17px] -translate-y-1/2 text-muted-foreground"
              fill="none"
              viewBox="0 0 17 10"
            >
              <path
                d="m8.499 9.626-7.538-7.538L2.2.85l6.3 6.3 6.3-6.3 1.238 1.238z"
                fill="currentColor"
              />
            </svg>
          </div>
        ) : null}
        <div className="mt-4 flex flex-col text-center">
          <Button asChild>
            <ContentLink
              href={selectedAnswer?.destination || 'https://www.nerdwallet.com'}
              target="_blank"
            >
              {ctaText}
            </ContentLink>
          </Button>
          <span className="typography-text-small mt-2 text-muted-foreground">
            on NerdWallet&apos;s secure site
          </span>
        </div>
      </div>
    </div>
  );
}
